import { useState, useMemo, useEffect, useRef } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { startOfDay, endOfDay } from "date-fns";
import { Settings, Download, SlidersHorizontal, Edit, Trash2, X } from "lucide-react";
import { getTradesByUser, updateTrade, useQuery, useAction } from "wasp/client/operations";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import { Button } from "../../client/components/ui/button";
import { Badge } from "../../client/components/ui/badge";
import { Star } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import { toast } from "../../client/hooks/use-toast";
import { ColumnConfigDialog } from "../../trading-journal/ColumnConfigDialog";
import AdvancedFiltersDialog, { type AdvancedFilters } from "../../trading-journal/AdvancedFiltersDialog";
import { exportTradesToCSV } from "../../trading-journal/csvImport";
import { parseCustomFieldValue } from "../../trading-journal/customFieldsUtils";
import { normalizeSymbol } from "../../shared/utils";
import { getTradePhase, type TradePhase } from "../../trading-accounts/tradePhase";
import { useDashboardData } from "./DashboardDataContext";
import { useRealtimeSync } from "../../client/hooks/useRealtimeSync";

// Date formatting helper — includes time to the second for scalpers
const formatDateTime = (date: Date): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
};

const PAGE_SIZE = 50;

/**
 * Cadence de rafraichissement du journal pour un utilisateur en temps reel.
 *
 * 20 s : assez court pour qu'un trade apparaisse sans que l'utilisateur pense
 * a recharger pendant une seance, assez long pour ne pas transformer chaque
 * onglet ouvert en source de trafic. La query etant PAGINEE, un
 * rafraichissement coute une page de 50 lignes, pas le journal entier.
 */
const REALTIME_JOURNAL_REFETCH_MS = 20_000;

export default function TradesTableWidget() {
  const {
    t,
    isDark,
    readOnly = false,
    accountFilter,
    strategyIds,
    dateRange,
    journalSymbol,
    journalSide,
    journalStatus,
    journalMinPnl,
    journalMaxPnl,
    journalTagIds,
    journalAdvancedFilters,
    setJournalAdvancedFilters,
    journalTags,
    journalCustomFields,
    journalColumnPreferences,
    accounts,
    strategies,
    isFreePlan = false,
    onEditTrade,
    onDeleteTrade,
    onBulkDeleteTrades,
    onColumnsChanged,
    onJournalTradesChanged,
  } = useDashboardData();

  const navigate = useNavigate();
  const updateTradeFn = useAction(updateTrade);

  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  // Mesure de la largeur disponible pour le masquage responsive des colonnes (purement visuel)
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const customFields = journalCustomFields ?? [];
  const columnPreferences = journalColumnPreferences ?? [];
  const accountsList: any[] = Array.isArray(accounts) ? accounts : [];

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [
    journalSymbol,
    journalSide,
    journalStatus,
    journalMinPnl,
    journalMaxPnl,
    JSON.stringify(journalTagIds),
    JSON.stringify(accountFilter),
    JSON.stringify(strategyIds),
    dateRange?.from,
    dateRange?.to,
  ]);

  // RAFRAICHISSEMENT COURT POUR LES UTILISATEURS EN TEMPS REEL.
  //
  // Le socket ecrit desormais les trades en quelques secondes, mais react-query
  // ne relit pas de lui-meme : sans cela, un betatesteur qui laisse son journal
  // ouvert verrait toujours ses trades apparaitre au rechargement de la page.
  // L'ecriture serait rapide et l'affichage lent, ce qui revient au meme pour
  // lui.
  //
  // 20 s : sous le seuil ou l'attente devient perceptible pendant une seance,
  // et la query est PAGINEE (`skip`/`take`), donc le cout d'un rafraichissement
  // reste celui d'une page de 50 lignes.
  //
  // `false` -- et non une cadence lente -- pour tous les autres : leurs donnees
  // ne changent qu'une fois par cycle de 15 minutes, poller ne relirait que la
  // meme chose.
  const { isLive: realtimeLive } = useRealtimeSync();

  const { data: tradesData, refetch } = useQuery(getTradesByUser, {
    skip: page * PAGE_SIZE,
    take: PAGE_SIZE,
    ...(journalSymbol && { symbol: journalSymbol }),
    ...(journalSide && journalSide !== "all" && { type: journalSide }),
    ...(journalStatus && journalStatus !== "all" && { status: journalStatus }),
    startDate: dateRange?.from ? startOfDay(dateRange.from) : undefined,
    endDate: dateRange?.to ? endOfDay(dateRange.to) : undefined,
    ...(accountFilter?.type === "account" && accountFilter.id
      ? { accountId: accountFilter.id }
      : accountFilter?.type === "group" && accountFilter.id
      ? { groupId: accountFilter.id }
      : accountFilter?.type === "none"
      ? { unassignedOnly: true }
      : {}),
    ...(strategyIds && strategyIds.length > 0 && { strategyIds }),
    ...(journalTagIds && journalTagIds.length > 0 && { tagIds: journalTagIds }),
    ...(journalMinPnl && { minPnl: parseFloat(journalMinPnl) }),
    ...(journalMaxPnl && { maxPnl: parseFloat(journalMaxPnl) }),
  }, {
    // Voir le commentaire au-dessus : cadence courte UNIQUEMENT pour les
    // utilisateurs dont le socket alimente reellement les donnees.
    refetchInterval: realtimeLive ? REALTIME_JOURNAL_REFETCH_MS : false,
  });

  const rawTrades: any[] = (tradesData as any)?.trades ?? [];
  const total: number = (tradesData as any)?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const trades = rawTrades;

  const triggerChanged = () => {
    refetch();
    onJournalTradesChanged?.();
  };

  // Calcule la phase d'un trade (challenge / funded / live / demo), null si pas de compte
  const computeTradePhase = (trade: any): TradePhase | null => {
    const acc = (trade as any).account;
    return acc
      ? getTradePhase(
          { date: new Date(trade.date) },
          { status: acc.status, fundedAt: acc.fundedAt ? new Date(acc.fundedAt) : null },
        )
      : null;
  };

  // Construire les colonnes visibles basées sur les préférences
  const visibleColumns = useMemo(() => {
    const preferenceMap = new Map(
      columnPreferences.map((p: any) => [p.columnKey, p]),
    );

    const STANDARD_COLUMNS: Array<{ key: string; label: string; required: boolean; isCustom?: boolean; isVisibleByDefault?: boolean }> = [
      { key: "date", label: t.journal.date, required: true, isVisibleByDefault: true },
      { key: "exitDate", label: t.journal.exitDate, required: false, isVisibleByDefault: true },
      { key: "symbol", label: t.journal.symbol, required: true, isVisibleByDefault: true },
      { key: "type", label: t.journal.type, required: true, isVisibleByDefault: true },
      { key: "status", label: t.journal.status, required: false, isVisibleByDefault: true },
      { key: "account", label: t.journal.account, required: false, isVisibleByDefault: true },
      { key: "entryPrice", label: t.journal.entryPrice, required: true, isVisibleByDefault: true },
      { key: "exitPrice", label: t.journal.exitPrice, required: false, isVisibleByDefault: true },
      { key: "quantity", label: t.journal.quantity, required: true, isVisibleByDefault: true },
      { key: "pnl", label: t.journal.pnl, required: false, isVisibleByDefault: true },
      { key: "swaps", label: t.journal.swaps, required: false, isVisibleByDefault: true },
      { key: "commission", label: t.journal.commission, required: false, isVisibleByDefault: true },
      { key: "strategy", label: t.journal.strategy, required: false, isVisibleByDefault: false },
      { key: "tags", label: t.journal.tags, required: false, isVisibleByDefault: false },
      { key: "notes", label: t.journal.notes, required: false, isVisibleByDefault: true },
      ...(!isFreePlan
        ? [{ key: "setupRating", label: t.journal.setupRating, required: false, isVisibleByDefault: true }]
        : []),
    ];

    const customCols = customFields.map((field: any) => ({
      key: `custom:${field.name}`,
      label: field.label,
      required: field.isRequired,
      isCustom: true,
      customFieldId: field.id,
      type: field.type as "text" | "number" | "date" | "boolean" | "tags",
      isVisibleByDefault: field.isVisibleByDefault,
    }));

    const allColumns = [...STANDARD_COLUMNS, ...customCols];

    const filtered = allColumns.filter((col) => {
      const pref = preferenceMap.get(col.key);
      if (pref) return pref.isVisible;
      return columnPreferences.length === 0 || col.required || (col as any).isVisibleByDefault !== false;
    });

    filtered.sort((a, b) => {
      const prefA = preferenceMap.get(a.key);
      const prefB = preferenceMap.get(b.key);
      if (prefA && prefB) return prefA.order - prefB.order;
      if (prefA) return -1;
      if (prefB) return 1;
      return 0;
    });

    return filtered;
  }, [columnPreferences, customFields, t, isFreePlan]);

  // Masquage responsive (purement visuel — ne touche jamais aux préférences sauvegardées).
  // On retire les colonnes les moins importantes quand le widget rétrécit, et on les
  // restaure quand il s'élargit. date / symbol / pnl ne sont jamais masquées.
  const { responsiveColumns, responsiveHiddenKeys } = useMemo(() => {
    const NEVER_HIDE = new Set(["date", "symbol", "pnl"]);
    const desiredKeys = visibleColumns.map((c) => c.key);
    const customKeysInDesired = desiredKeys.filter((k) => k.startsWith("custom:"));

    // Ordre de masquage : index 0 = masqué en premier.
    // Tier 1 : champs custom, notes, swaps, commission.
    // Tier 2 : exitDate, setupRating, strategy, account, exitPrice, entryPrice.
    // Tier 3 (masqués en dernier) : type, status, quantity.
    const hideOrder = [
      ...customKeysInDesired,
      "notes",
      "swaps",
      "commission",
      "exitDate",
      "setupRating",
      "strategy",
      "account",
      "exitPrice",
      "entryPrice",
      "type",
      "status",
      "quantity",
    ].filter((k) => desiredKeys.includes(k));

    // containerWidth === 0 → pas encore mesuré : on affiche tout (évite un flash de sur-masquage).
    const overhead = 80; // colonne checkbox + colonne actions
    const perCol = 110;
    const maxCols =
      containerWidth === 0
        ? Infinity
        : Math.max(NEVER_HIDE.size, Math.floor((containerWidth - overhead) / perCol));

    const hiddenSet = new Set<string>();
    if (desiredKeys.length > maxCols) {
      for (const key of hideOrder) {
        if (NEVER_HIDE.has(key)) continue;
        hiddenSet.add(key);
        if (desiredKeys.length - hiddenSet.size <= maxCols) break;
      }
    }

    const cols = visibleColumns.filter((c) => !hiddenSet.has(c.key));
    return { responsiveColumns: cols, responsiveHiddenKeys: Array.from(hiddenSet) };
  }, [visibleColumns, containerWidth]);

  // Fonction helper pour rendre une cellule selon le type de colonne
  const renderCell = (column: any, trade: any) => {
    if (column.isCustom) {
      const fieldDef = customFields.find((f: any) => f.id === column.customFieldId);
      if (!fieldDef) return <td className="p-2">-</td>;

      const customValue = ((trade as any).TradeCustomFieldValue || []).find(
        (v: any) => v.field?.id === fieldDef.id || v.fieldId === fieldDef.id,
      );

      if (!customValue) return <td className="p-2">-</td>;

      const value = parseCustomFieldValue(
        {
          id: fieldDef.id,
          name: fieldDef.name,
          label: fieldDef.label,
          type: (fieldDef.type as string) as "text" | "number" | "date" | "boolean" | "tags",
          isRequired: fieldDef.isRequired,
        },
        customValue as any,
      );

      if (fieldDef.type === "tags") {
        const tags = Array.isArray(value) ? value : [];
        return (
          <td className="p-2">
            <div className="flex flex-wrap gap-1">
              {tags.map((tag: string, idx: number) => (
                <span
                  key={idx}
                  className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800"
                >
                  {tag}
                </span>
              ))}
            </div>
          </td>
        );
      } else if (fieldDef.type === "boolean") {
        return <td className="p-2">{value ? "✓" : "✗"}</td>;
      } else if (fieldDef.type === "date") {
        return <td className="p-2">{value ? formatDateTime(new Date(value)) : "-"}</td>;
      } else if (fieldDef.type === "number") {
        return (
          <td className="p-2 text-right">
            {value !== null && value !== undefined ? Number(value).toFixed(2) : "-"}
          </td>
        );
      } else {
        return <td className="p-2">{value || "-"}</td>;
      }
    }

    switch (column.key) {
      case "date":
        return (
          <td className="p-2 font-mono text-xs">{formatDateTime(new Date(trade.date))}</td>
        );
      case "exitDate":
        return (
          <td className="p-2 font-mono text-xs">
            {trade.exitDate ? formatDateTime(new Date(trade.exitDate)) : "-"}
          </td>
        );
      case "symbol":
        return (
          <td className="p-2 font-medium">
            <span className="inline-flex items-center gap-1">
              {normalizeSymbol(trade.symbol)}
              {(trade as any).source === "metaapi" && (
                <Badge
                  variant="outline"
                  className="text-[10px] h-4 px-1 text-primary border-primary/30 bg-primary/10"
                >
                  MT
                </Badge>
              )}
            </span>
          </td>
        );
      case "type":
        return (
          <td className="p-2">
            <span
              className={`px-2 py-1 rounded text-xs ${
                trade.type === "long"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {trade.type}
            </span>
          </td>
        );
      case "status":
        return (
          <td className="p-2">
            <span
              className={`px-2 py-1 rounded text-xs ${
                (trade.status as string) === "open"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {(trade.status as string) === "open" ? t.journal.open : t.journal.closed}
            </span>
          </td>
        );
      case "account": {
        const tradePhase = computeTradePhase(trade);
        return (
          <td className="p-2">
            <div className="flex flex-col items-start gap-1">
              {readOnly ? (
                <span className="text-xs">{(trade as any).account?.name || "—"}</span>
              ) : (
                <Select
                  value={(trade as any).account?.id || "none"}
                  onValueChange={async (value) => {
                    try {
                      await updateTradeFn({
                        id: trade.id,
                        accountId: value === "none" ? null : value,
                      });
                      triggerChanged();
                      toast({
                        title: t.journal.accountUpdatedTrade,
                        description: t.journal.accountUpdatedTradeDescription,
                      });
                    } catch (error: any) {
                      toast({
                        title: t.common.error,
                        description: error.message || t.journal.cannotUpdateAccount,
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-[140px] h-8 text-xs" onClick={(e) => e.stopPropagation()}>
                    <SelectValue placeholder="Aucun compte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun compte</SelectItem>
                    {accountsList.map((account: any) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {tradePhase && (
                <Badge variant="outline" className="text-xs">
                  {tradePhase === "challenge" && t.tradingAccounts.phaseChallenge}
                  {tradePhase === "funded" && t.tradingAccounts.phaseFunded}
                  {tradePhase === "live" && t.tradingAccounts.phaseLive}
                  {tradePhase === "demo" && t.tradingAccounts.phaseDemo}
                </Badge>
              )}
            </div>
          </td>
        );
      }
      case "entryPrice":
        return <td className="p-2 text-right">{trade.entryPrice.toFixed(2)}</td>;
      case "exitPrice":
        return <td className="p-2 text-right">{trade.exitPrice?.toFixed(2) || "-"}</td>;
      case "quantity":
        return <td className="p-2 text-right">{trade.quantity.toFixed(2)}</td>;
      case "pnl":
        return (
          <td
            className={`p-2 text-right font-bold font-mono text-base ${
              trade.pnl === null || trade.pnl === undefined
                ? "text-muted-foreground"
                : trade.pnl >= 0
                ? "text-pnlPositive"
                : "text-pnlNegative"
            }`}
          >
            {trade.pnl === null || trade.pnl === undefined
              ? "-"
              : `${trade.pnl >= 0 ? "+" : ""}${trade.pnl.toFixed(2)} $`}
          </td>
        );
      case "swaps":
        return (
          <td className="p-2 text-right">
            {(trade as any).swaps === null || (trade as any).swaps === undefined
              ? "-"
              : `${(trade as any).swaps >= 0 ? "+" : ""}${(trade as any).swaps.toFixed(2)} $`}
          </td>
        );
      case "commission":
        return <td className="p-2 text-right">{trade.commission.toFixed(2)}</td>;
      case "strategy": {
        const tradeStrategies: any[] = (trade as any).strategies ?? [];
        const accountStrategies: any[] = (trade as any).account?.strategies ?? [];
        const tradeStrats = tradeStrategies.length > 0 ? tradeStrategies : accountStrategies;
        return (
          <td className="p-2">
            {tradeStrats.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {tradeStrats.map((s: any) => (
                  <Badge key={s.id} variant="outline" className="text-xs font-normal">
                    {s.name}
                  </Badge>
                ))}
              </div>
            ) : (
              "-"
            )}
          </td>
        );
      }
      case "tags": {
        const tradeTags: any[] = (trade as any).tags ?? [];
        if (tradeTags.length === 0) {
          return <td className="p-2">-</td>;
        }
        // Tronqué à 2 pastilles pour ne pas casser la hauteur de ligne.
        const visibleTags = tradeTags.slice(0, 2);
        const hiddenCount = tradeTags.length - visibleTags.length;
        return (
          <td className="p-2">
            <div className="flex flex-wrap gap-1">
              {visibleTags.map((tag: any) => (
                <Badge key={tag.id} variant="secondary" className="text-xs font-normal">
                  {tag.name}
                </Badge>
              ))}
              {hiddenCount > 0 && (
                <Badge variant="outline" className="text-xs font-normal">
                  +{hiddenCount}
                </Badge>
              )}
            </div>
          </td>
        );
      }
      case "notes":
        return <td className="p-2">{trade.notes || "-"}</td>;
      case "setupRating":
        return (
          <td className="p-2">
            {(trade as any).setupRating ? (
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-3.5 w-3.5 ${
                      star <= (trade as any).setupRating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground/30"
                    }`}
                  />
                ))}
              </div>
            ) : (
              "-"
            )}
          </td>
        );
      default:
        return <td className="p-2">-</td>;
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    onBulkDeleteTrades?.(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleExport = () => {
    if (rawTrades.length === 0) {
      toast({
        title: t.journal.noValidTrades,
        description: t.journal.noTradesExport,
        variant: "destructive",
      });
      return;
    }
    const csv = exportTradesToCSV(rawTrades);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    link.setAttribute("download", `trades_${dateStr}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const headerButtons = (
    <div className="flex items-center gap-1.5">
      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setIsAdvancedOpen(true)}>
        <SlidersHorizontal className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">{t.journal.advancedFilters}</span>
      </Button>
      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setIsColumnConfigOpen(true)}>
        <Settings className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">{t.journal.columnsLabel}</span>
      </Button>
      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={handleExport}>
        <Download className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">{t.journal.export}</span>
      </Button>
    </div>
  );

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.journal.tradesListWidget}
        isDark={isDark}
        compact
        rightContent={headerButtons}
      />
      <CardContent className="px-0 pb-0 pt-0 flex-1 overflow-auto min-h-0">
        {!readOnly && selectedIds.size > 0 && (
          <div className="flex items-center gap-3 m-2 px-2 py-2 rounded-lg bg-muted/60 border">
            <span className="text-sm font-medium">
              {t.journal.selectedCount.replace("{count}", String(selectedIds.size))}
            </span>
            <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
              <Trash2 className="h-4 w-4 mr-1" />
              {t.journal.deleteTrade}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
              <X className="h-4 w-4 mr-1" />
              {t.journal.deselectAll}
            </Button>
          </div>
        )}
        <div className="overflow-x-auto" ref={containerRef}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                {!readOnly && (
                  <th className="w-8 p-2">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={trades.length > 0 && trades.every((tr) => selectedIds.has(tr.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(trades.map((tr) => tr.id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                    />
                  </th>
                )}
                {responsiveColumns.map((column) => (
                  <th
                    key={column.key}
                    className={
                      column.key === "entryPrice" ||
                      column.key === "exitPrice" ||
                      column.key === "quantity" ||
                      column.key === "pnl" ||
                      column.key === "swaps" ||
                      column.key === "commission" ||
                      ((column as any).isCustom && (column as any).type === "number")
                        ? "text-right p-2"
                        : "text-left p-2"
                    }
                  >
                    {column.label}
                  </th>
                ))}
                {!readOnly && <th className="text-right p-2">{t.common.actions}</th>}
              </tr>
            </thead>
            <tbody>
              {trades.map((trade) => (
                <tr
                  key={trade.id}
                  className={`border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${
                    selectedIds.has(trade.id) ? "bg-primary/5" : ""
                  }`}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (
                      target.closest("button") ||
                      target.closest("a") ||
                      target.closest('[role="button"]') ||
                      target.closest('input[type="checkbox"]')
                    ) {
                      return;
                    }
                    navigate(`/trade/${trade.id}`);
                  }}
                >
                  {!readOnly && (
                    <td className="w-8 p-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        checked={selectedIds.has(trade.id)}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) next.add(trade.id);
                          else next.delete(trade.id);
                          setSelectedIds(next);
                        }}
                      />
                    </td>
                  )}
                  {responsiveColumns.map((column) => (
                    <React.Fragment key={column.key}>{renderCell(column, trade)}</React.Fragment>
                  ))}
                  {!readOnly && (
                    <td className="p-2">
                      <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => onEditTrade?.(trade)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onDeleteTrade?.(trade)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="m-2 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {page + 1} / {totalPages} ({total})
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                {t.common.previous}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                {t.common.next}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        spaceHiddenKeys={responsiveHiddenKeys}
        onConfigChange={() => {
          onColumnsChanged?.();
          refetch();
        }}
      />

      <AdvancedFiltersDialog
        open={isAdvancedOpen}
        onOpenChange={setIsAdvancedOpen}
        filters={
          (journalAdvancedFilters as AdvancedFilters) ?? {
            accountId: "",
            groupId: "",
            strategyIds: [],
            status: "all",
            tagIds: [],
            minPnl: "",
            maxPnl: "",
            customFields: {},
          }
        }
        onFiltersChange={(f) => setJournalAdvancedFilters?.(f)}
        onApply={() => setIsAdvancedOpen(false)}
        onReset={() =>
          setJournalAdvancedFilters?.({
            accountId: "",
            groupId: "",
            strategyIds: [],
            status: "all",
            tagIds: [],
            minPnl: "",
            maxPnl: "",
            customFields: {},
          })
        }
        tags={journalTags ?? []}
        accounts={accountsList}
        strategies={(strategies as any[]) ?? []}
        customFields={customFields}
        customFieldTags={{}}
        hideAccountAndCustomFields
      />
    </Card>
  );
}
